/**
 * The Correspondents screen's sidebar forms, on the panel-form standard
 * (FORM_STYLE_GUIDE.md §12b): pinned header · scrolling body · pinned footer,
 * sections declared as data and rendered by panelFormModel.
 *
 *   CorrespondentContactPanel   Contacts tab — every slot (primary contact,
 *                               MLPA signatory, additional contacts,
 *                               authorized signers, servicing contacts)
 *   CorrespondentOverlayPanel   Overlays tab — new / edit guideline overlay
 *   CorrespondentUploadPanel    Agreements tab — upload a document version
 *   AddCorrespondentPanel       List screen — create a correspondent
 *
 * They replace the 520px `SideDrawer` forms. Each is mounted only while open
 * (the caller renders it conditionally), so its draft always starts from the
 * record it was opened on.
 */
import { useCallback, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import BusinessOutlined from "@mui/icons-material/BusinessOutlined";
import CloseRounded from "@mui/icons-material/CloseRounded";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import PersonOutlined from "@mui/icons-material/PersonOutlined";
import UploadFileOutlined from "@mui/icons-material/UploadFileOutlined";

import ResizeableSidebar from "../../../components/sidebar/ResizeableSidebar";
import Txt from "../../../components/typography/Txt";
import {
  FormField,
  FormPanelFooter,
  FormTextField,
  FormWarn,
} from "../../../components/forms/FormPrimitives";
import {
  PanelSections,
  SCOPE,
  changedOnly,
  countRequiredFilled,
  isFilled,
  prune,
  requiredOf,
} from "../../../components/forms/panelFormModel";
import { FORM, formSx, px } from "../../../theme/formStyles";
import {
  AGREEMENT_CATEGORIES,
  AGREEMENT_STAGES,
  CORRESPONDENT_CONTACT_ROLES,
  DOCUMENT_CATEGORIES,
  ONBOARDING_STATUSES,
  oidOf,
  useCorrespondentsApi,
} from "../../../hooks/useCorrespondentsHook";

const PANEL_WIDTH = 720;

// DSCR_TIER_NAMES on the backend model (pricingTier options), verbatim.
const PRICING_TIERS = ["Gold", "Silver", "Bronze"];

const errorText = (e, fallback) =>
  e?.response?.data?.detail || e?.message || fallback;

/* ─────────────────────────────── the shell ─────────────────────────────── */

/**
 * One draft + the counts every panel footer needs. `sections` may depend on
 * the values (a field that only exists for agreements), so it is a function
 * of ctx.
 */
const usePanelDraft = (initialValues, sectionsOf) => {
  const [values, setValues] = useState(initialValues);
  const setValue = useCallback(
    (name, v) => setValues((prev) => ({ ...prev, [name]: v })),
    [],
  );
  const ctx = useMemo(() => ({ values }), [values]);
  const sections = useMemo(() => sectionsOf(ctx), [sectionsOf, ctx]);
  const required = useMemo(() => requiredOf(sections, ctx), [sections, ctx]);
  const requiredFilled = countRequiredFilled(required, values);
  return {
    values,
    setValue,
    ctx,
    sections,
    required,
    requiredFilled,
    requiredMet: requiredFilled === required.length,
  };
};

function CorrespondentPanel({
  title,
  subtitle,
  icon,
  onClose,
  error,
  footer,
  children,
}) {
  return (
    <ResizeableSidebar
      open
      hideBackdrop
      onClose={onClose}
      title={title}
      titleIcon={<Box sx={formSx.panelHeadIcon}>{icon}</Box>}
      descriptionContent={
        subtitle ? <Txt sx={formSx.titleHelper}>{subtitle}</Txt> : null
      }
      defaultWidth={PANEL_WIDTH}
      sx={formSx.panelContainer}
      headerSx={formSx.panelHead}
      bodyPadding={0}
      bodySx={formSx.panelBody}
      footerContent={footer}
    >
      {error ? (
        <Box sx={{ mb: px(FORM.s.s5) }}>
          <FormWarn>
            <strong>Couldn&apos;t save.</strong> {error}
          </FormWarn>
        </Box>
      ) : null}
      {children}
    </ResizeableSidebar>
  );
}

CorrespondentPanel.propTypes = {
  title: PropTypes.node,
  subtitle: PropTypes.node,
  icon: PropTypes.node,
  onClose: PropTypes.func,
  error: PropTypes.node,
  footer: PropTypes.node,
  children: PropTypes.node,
};

const PanelActions = ({ onClose, disabled, onSave, label, busy }) => (
  <>
    <Button variant="outlined" color="gray" sx={formSx.button} onClick={onClose}>
      Cancel
    </Button>
    <Button sx={formSx.button} disabled={disabled || busy} onClick={onSave}>
      {busy ? "Saving…" : label}
    </Button>
  </>
);

PanelActions.propTypes = {
  onClose: PropTypes.func,
  disabled: PropTypes.bool,
  onSave: PropTypes.func,
  label: PropTypes.node,
  busy: PropTypes.bool,
};

/* ─────────────────────────────── contacts ──────────────────────────────── */

// The CorrespondentContact fields this form owns. Everything else on the
// record (oid, correspondent_id, createDateTime, address, bankDetails) is left
// alone: a slot edit posts only what changed, and a single-object slot is
// re-posted with the untouched keys carried through.
const CONTACT_FIELDS = [
  "firstName",
  "lastName",
  "title",
  "role",
  "emailAddress",
  "phone",
  "company",
];

// "Ada Lovelace" → { firstName: "Ada", lastName: "Lovelace" }. The last word
// is the last name and everything before it the first; "Lovelace, Ada" (a
// comma) reads the other way round. One word is a first name.
export const splitFullName = (raw) => {
  const text = String(raw ?? "").replace(/\s+/g, " ").trim();
  if (!text) return { firstName: "", lastName: "" };
  if (text.includes(",")) {
    const [last, ...rest] = text.split(",");
    return { firstName: rest.join(",").trim(), lastName: last.trim() };
  }
  const parts = text.split(" ");
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  return { firstName: parts.slice(0, -1).join(" "), lastName: parts[parts.length - 1] };
};

/**
 * One "Full name" box shown while first AND last name are both empty. On blur
 * it splits what was typed / pasted into firstName + lastName, which swaps the
 * form over to the two separate inputs. The text lives in local state — it is
 * never part of the saved values, so nothing called "fullName" is posted.
 */
function FullNameInput({ field, setNames }) {
  const [text, setText] = useState("");
  const commit = () => {
    const { firstName, lastName } = splitFullName(text);
    if (!firstName && !lastName) return;
    setNames({ firstName, lastName });
    setText("");
  };
  return (
    <FormTextField
      label={field.label}
      span={field.span}
      value={text}
      onChange={setText}
      placeholder={field.placeholder}
      helperText={field.helperText}
      inputProps={{
        onBlur: commit,
        onKeyDown: (e) => e.key === "Enter" && commit(),
      }}
    />
  );
}

FullNameInput.propTypes = {
  field: PropTypes.object,
  setNames: PropTypes.func,
};

const noNames = ({ values }) =>
  !isFilled(values.firstName) && !isFilled(values.lastName);

const contactSections = ({ defaultsRole, setNamesRef }) => () => [
  {
    key: "person",
    title: "Person",
    helper: "Who they are and what they do for the correspondent.",
    collapseWhen: SCOPE.ALL,
    fields: [
      // Full name while both parts are empty; first / last once either is set.
      {
        name: "fullName",
        label: "Full name",
        span: 6,
        placeholder: "e.g. Ada Lovelace",
        visibleWhen: noNames,
        render: ({ field }) => (
          <FullNameInput
            field={field}
            setNames={(names) => setNamesRef.current?.(names)}
          />
        ),
      },
      {
        name: "firstName",
        label: "First name",
        span: 3,
        visibleWhen: (ctx) => !noNames(ctx),
      },
      {
        name: "lastName",
        label: "Last name",
        span: 3,
        visibleWhen: (ctx) => !noNames(ctx),
      },
      {
        name: "title",
        label: "Title",
        span: 6,
        placeholder: "e.g. Director of Capital Markets",
      },
      {
        name: "role",
        label: "Role",
        kind: "chips",
        span: 6,
        options: CORRESPONDENT_CONTACT_ROLES,
        // add_contact defaults a missing role to Correspondent; the
        // single-object slots and edits have no such default.
        helperText: defaultsRole
          ? "Left blank, it's saved as Correspondent."
          : undefined,
      },
    ],
  },
  {
    key: "reach",
    title: "Contact details",
    helper: "How to reach them.",
    collapseWhen: SCOPE.ALL,
    fields: [
      { name: "emailAddress", label: "Email", kind: "email", span: 3 },
      { name: "phone", label: "Phone", kind: "tel", span: 3 },
      { name: "company", label: "Company", span: 6 },
    ],
  },
];

/**
 * Add / edit one correspondent contact.
 *
 * `mode="slot"` — an array slot (additional contacts, authorized signers,
 * servicing contacts) written through the typed contact routes: a create
 * posts the filled fields, an edit posts only what changed. The old form
 * re-posted every field on edit, so saving an untouched contact matched
 * nothing to modify and came back "contact not found in that slot".
 *
 * `mode="object"` — primary contact / MLPA signatory, single objects written
 * through the company update, so the whole contact is posted.
 *
 * `onSave(payload)` returns a promise; a rejection shows in the panel.
 */
export function CorrespondentContactPanel({
  title,
  subtitle,
  initial,
  mode = "slot",
  onSave,
  onClose,
}) {
  const isEdit = mode === "slot" ? !!oidOf(initial) : !!initial;
  const initialValues = useMemo(
    () =>
      Object.fromEntries(
        CONTACT_FIELDS.map((k) => [k, initial?.[k] ?? null]),
      ),
    [initial],
  );
  // The Full name box writes both name fields at once; it reaches setValue
  // through this ref because the sections are declared before the draft.
  const setNamesRef = useRef(null);
  const sectionsOf = useMemo(
    () =>
      contactSections({ defaultsRole: mode === "slot" && !isEdit, setNamesRef }),
    [mode, isEdit],
  );
  const { values, setValue, ctx, sections } = usePanelDraft(
    initialValues,
    sectionsOf,
  );
  setNamesRef.current = ({ firstName, lastName }) => {
    setValue("firstName", firstName || null);
    setValue("lastName", lastName || null);
  };
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // No field is required on the model, but a contact with nothing in it is
  // not a contact — the button waits for at least one answer.
  const anyFilled = CONTACT_FIELDS.some((k) => isFilled(values[k]));
  const changes = changedOnly(values, initialValues);
  const hasChanges = !isEdit || Object.keys(changes).length > 0;
  const canSave = anyFilled && hasChanges;

  const save = async () => {
    if (!canSave) return;
    let payload;
    if (mode === "object") payload = prune({ ...(initial || {}), ...values });
    else payload = isEdit ? changes : prune(values);
    setBusy(true);
    setError(null);
    try {
      await onSave(payload);
    } catch (e) {
      setError(errorText(e, "Failed to save the contact."));
      setBusy(false);
    }
  };

  return (
    <CorrespondentPanel
      title={title}
      subtitle={subtitle}
      icon={<PersonOutlined />}
      onClose={onClose}
      error={error}
      footer={
        <FormPanelFooter
          readyText={
            !anyFilled
              ? "Nothing entered yet"
              : hasChanges
                ? "Ready to save"
                : "No changes yet"
          }
        >
          <PanelActions
            onClose={onClose}
            onSave={save}
            disabled={!canSave}
            busy={busy}
            label={isEdit ? "Save changes" : "Add contact"}
          />
        </FormPanelFooter>
      }
    >
      <PanelSections sections={sections} ctx={ctx} setValue={setValue} />
    </CorrespondentPanel>
  );
}

CorrespondentContactPanel.propTypes = {
  title: PropTypes.node,
  subtitle: PropTypes.node,
  initial: PropTypes.object,
  mode: PropTypes.oneOf(["slot", "object"]),
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

/* ─────────────────────────────── overlays ──────────────────────────────── */

/**
 * The overlay's checklist — a list of free-text requirements, added one at a
 * time. A one-off control (FORM_STYLE_GUIDE.md §12b `render`): rows are the
 * static 44px `fileRow` inside a `listGroup`, the adder is a form input plus
 * an outlined button.
 */
function ChecklistEditor({ field, value, onChange }) {
  const items = Array.isArray(value) ? value : [];
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v || items.includes(v)) return;
    onChange([...items, v]);
    setDraft("");
  };
  return (
    <FormField label={field.label} span={field.span} helperText={field.helperText}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: px(FORM.s.s2) }}>
        {items.length ? (
          <Box sx={formSx.listGroup}>
            {items.map((item, i) => (
              <Box key={`${item}-${i}`} sx={formSx.fileRow}>
                <Txt sx={{ ...formSx.body, flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>
                  {item}
                </Txt>
                <IconButton
                  size="small"
                  aria-label={`Remove ${item}`}
                  onClick={() => onChange(items.filter((_, j) => j !== i))}
                >
                  <CloseRounded fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        ) : null}
        <Box sx={{ display: "flex", gap: px(FORM.s.s2), alignItems: "flex-start" }}>
          <TextField
            size="small"
            fullWidth
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                add();
              }
            }}
            placeholder="Add a requirement…"
            sx={formSx.input}
          />
          <Button
            variant="outlined"
            color="gray"
            sx={{ ...formSx.button, flexShrink: 0 }}
            disabled={!draft.trim()}
            onClick={add}
          >
            Add
          </Button>
        </Box>
      </Box>
    </FormField>
  );
}

ChecklistEditor.propTypes = {
  field: PropTypes.object,
  value: PropTypes.array,
  onChange: PropTypes.func,
};

const overlaySections = () => [
  {
    key: "overlay",
    title: "Overlay",
    helper:
      "What this partner layers on top of the base product guidelines. Line breaks in the description are kept.",
    collapseWhen: SCOPE.ALL,
    fields: [
      {
        name: "overlayName",
        label: "Overlay name",
        span: 6,
        req: true,
        placeholder: "e.g. UW Notes",
      },
      {
        name: "overlayDescription",
        label: "Description",
        kind: "textarea",
        span: 6,
        minRows: 6,
        placeholder: "Line breaks, bullets and indentation are preserved.",
      },
      {
        name: "referenceGuideline",
        label: "Reference guideline",
        span: 6,
        optional: true,
      },
    ],
  },
  {
    key: "checklist",
    title: "Underwriting checklist",
    helper:
      "Requirements added to underwriting on every new loan for this partner.",
    // An overlay with no checklist items is complete — don't count it.
    showCount: false,
    fields: [
      {
        name: "checkListItems",
        label: "Checklist items",
        span: 6,
        optional: true,
        render: (props) => <ChecklistEditor {...props} />,
        summary: (v) => `${v.length} item${v.length === 1 ? "" : "s"}`,
      },
    ],
  },
];

/** New / edit a guideline overlay. `overlayId` addresses an existing one. */
export function CorrespondentOverlayPanel({
  cid,
  overlay,
  overlayId,
  subtitle,
  onClose,
  onSaved,
}) {
  const correspondentsApi = useCorrespondentsApi();
  const isEdit = !!overlayId;
  const initialValues = useMemo(
    () => ({
      overlayName: overlay?.overlayName ?? "",
      overlayDescription: overlay?.overlayDescription ?? "",
      referenceGuideline: overlay?.referenceGuideline ?? "",
      checkListItems: [...(overlay?.checkListItems ?? [])],
    }),
    [overlay],
  );
  const { values, setValue, ctx, sections, required, requiredFilled } =
    usePanelDraft(initialValues, overlaySections);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const nameOk = !!values.overlayName?.trim();
  const changes = changedOnly(values, initialValues);
  const hasChanges = !isEdit || Object.keys(changes).length > 0;
  const canSave = nameOk && hasChanges;

  const save = async () => {
    if (!canSave) return;
    const trimmed = { ...values, overlayName: values.overlayName.trim() };
    setBusy(true);
    setError(null);
    try {
      if (isEdit) {
        const body = changedOnly(trimmed, initialValues);
        // A cleared reference goes up as null, as it always has.
        if (body.referenceGuideline === "") body.referenceGuideline = null;
        await correspondentsApi.updateOverlay(cid, overlayId, body);
      } else {
        await correspondentsApi.createOverlay(cid, prune(trimmed));
      }
      onSaved();
    } catch (e) {
      setError(errorText(e, "Failed to save the overlay."));
      setBusy(false);
    }
  };

  return (
    <CorrespondentPanel
      title={isEdit ? "Edit Overlay" : "New Overlay"}
      subtitle={subtitle}
      icon={<DescriptionOutlined />}
      onClose={onClose}
      error={error}
      footer={
        <FormPanelFooter
          required={required.length}
          filled={nameOk ? requiredFilled : 0}
          readyText={hasChanges ? "Ready to save" : "No changes yet"}
        >
          <PanelActions
            onClose={onClose}
            onSave={save}
            disabled={!canSave}
            busy={busy}
            label={isEdit ? "Save changes" : "Create overlay"}
          />
        </FormPanelFooter>
      }
    >
      <PanelSections sections={sections} ctx={ctx} setValue={setValue} />
    </CorrespondentPanel>
  );
}

CorrespondentOverlayPanel.propTypes = {
  cid: PropTypes.string,
  overlay: PropTypes.object,
  overlayId: PropTypes.string,
  subtitle: PropTypes.node,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

/* ─────────────────────────────── documents ─────────────────────────────── */

const isAgreementCategory = (c) => AGREEMENT_CATEGORIES.includes(c);

const DOCUMENT_CATEGORIES_AZ = [...DOCUMENT_CATEGORIES].sort((a, b) =>
  a.localeCompare(b),
);

const uploadSections = (versions) => (ctx) => {
  const file = ctx.values.uploadMetadata?.[0];
  const agreement = isAgreementCategory(ctx.values.category);
  return [
    {
      key: "document",
      title: "Document",
      helper: "The file and what it is.",
      collapseWhen: SCOPE.REQUIRED,
      fields: [
        {
          name: "uploadMetadata",
          label: "File",
          kind: "file",
          span: 6,
          req: true,
          placeholder: "Drop the document here or",
          hint: "One file",
        },
        {
          name: "category",
          label: "Category",
          // Type-ahead, A–Z: the list is long enough to search.
          kind: "combo",
          span: 6,
          req: true,
          options: DOCUMENT_CATEGORIES_AZ,
          helperText: agreement
            ? 'Version numbers count up per category; the status starts as "Proposed".'
            : undefined,
        },
        {
          name: "file_display_name",
          label: "Display name",
          span: 6,
          optional: true,
          placeholder: file?.name || "Defaults to the file name",
        },
      ],
    },
    ...(agreement
      ? [
          {
            key: "agreement",
            title: "Agreement",
            helper: "Where this turn sits in the negotiation.",
            collapseWhen: SCOPE.ALL,
            fields: [
              {
                name: "agreementStage",
                label: "Stage",
                kind: "chips",
                span: 6,
                options: AGREEMENT_STAGES,
              },
              {
                name: "supersedesDocumentId",
                label: "Supersedes",
                kind: "select",
                span: 6,
                placeholder: "Nothing",
                options: [
                  { value: "", label: "Nothing" },
                  ...versions.map((v) => ({
                    value: oidOf(v),
                    label: `v${v.agreementVersion} · ${v.file_display_name}`,
                  })),
                ],
                helperText: "Sets the lineage the diff walks back through.",
                summary: (id) => {
                  const v = versions.find((x) => oidOf(x) === id);
                  return v ? `Supersedes v${v.agreementVersion}` : null;
                },
              },
            ],
          },
        ]
      : []),
    {
      key: "notes",
      title: "Notes",
      helper: "Anything a reviewer should know about this upload.",
      collapseWhen: SCOPE.ALL,
      showCount: false,
      fields: [
        {
          name: "notes",
          label: "Notes",
          kind: "textarea",
          span: 6,
          minRows: 2,
          optional: true,
        },
      ],
    },
  ];
};

/**
 * Upload a correspondent document. Agreement categories version per
 * category, so they also ask for the stage and the version it supersedes;
 * those two are only sent for an agreement, as before.
 */
export function CorrespondentUploadPanel({
  cid,
  defaultCategory,
  // A File already picked (e.g. dropped on the Agreements tab) — opens the
  // panel with it in the File field.
  initialFile,
  versions = [],
  subtitle,
  onClose,
  onUploaded,
}) {
  const correspondentsApi = useCorrespondentsApi();
  const sectionsOf = useMemo(() => uploadSections(versions), [versions]);
  const initialValues = useMemo(
    () => ({
      category: defaultCategory || null,
      ...(initialFile
        ? {
            uploadMetadata: [
              {
                id: `${initialFile.name}-${initialFile.lastModified}`,
                file: initialFile,
                name: initialFile.name,
                file_display_name: initialFile.name,
                docGroup: null,
                docType: null,
              },
            ],
          }
        : {}),
    }),
    [defaultCategory, initialFile],
  );
  const { values, setValue, ctx, sections, required, requiredFilled, requiredMet } =
    usePanelDraft(initialValues, sectionsOf);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    const file = values.uploadMetadata?.[0]?.file;
    if (!requiredMet || !file) return;
    const agreement = isAgreementCategory(values.category);
    const submission = prune({
      category: values.category,
      file_display_name: values.file_display_name,
      notes: values.notes,
      agreementStage: agreement ? values.agreementStage : null,
      supersedesDocumentId: agreement ? values.supersedesDocumentId : null,
    });
    setBusy(true);
    setError(null);
    try {
      // The response is the updated correspondent (the new document is the
      // last entry of `documents`) — passed on for callers that link it.
      const res = await correspondentsApi.uploadDocument(cid, file, submission);
      onUploaded(res);
    } catch (e) {
      setError(errorText(e, "Upload failed."));
      setBusy(false);
    }
  };

  return (
    <CorrespondentPanel
      title="Upload Document"
      subtitle={subtitle}
      icon={<UploadFileOutlined />}
      onClose={onClose}
      error={error}
      footer={
        <FormPanelFooter
          required={required.length}
          filled={requiredFilled}
          readyText="Ready to upload"
        >
          <PanelActions
            onClose={onClose}
            onSave={save}
            disabled={!requiredMet}
            busy={busy}
            label="Upload"
          />
        </FormPanelFooter>
      }
    >
      <PanelSections sections={sections} ctx={ctx} setValue={setValue} />
    </CorrespondentPanel>
  );
}

CorrespondentUploadPanel.propTypes = {
  cid: PropTypes.string,
  defaultCategory: PropTypes.string,
  initialFile: PropTypes.object,
  versions: PropTypes.array,
  subtitle: PropTypes.node,
  onClose: PropTypes.func.isRequired,
  onUploaded: PropTypes.func.isRequired,
};

/* ─────────────────────────── add correspondent ─────────────────────────── */

const addCorrespondentSections = () => [
  {
    key: "company",
    title: "Company",
    helper: "Everything else — contacts, coverage, pricing — is set on the correspondent once it exists.",
    collapseWhen: SCOPE.ALL,
    fields: [
      { name: "companyName", label: "Company name", span: 6, req: true },
      {
        name: "pricingTier",
        label: "Pricing tier",
        kind: "chips",
        span: 6,
        options: PRICING_TIERS,
        helperText: "The DSCR margin tier this partner prices at.",
      },
      {
        name: "onboardingStatus",
        label: "Relationship status",
        kind: "chips",
        span: 6,
        options: ONBOARDING_STATUSES,
        helperText:
          "Onboarding while the agreement and setup are in progress; Active once they are selling to EV.",
      },
      {
        name: "companyWebsite",
        label: "Website",
        span: 6,
        placeholder: "e.g. https://example.com",
      },
      {
        name: "companyNotes",
        label: "Notes",
        kind: "textarea",
        span: 6,
        optional: true,
      },
    ],
  },
];

/** Create a correspondent. `onCreated(created)` fires with the new record. */
export function AddCorrespondentPanel({ onClose, onCreated }) {
  const correspondentsApi = useCorrespondentsApi();
  const initialValues = useMemo(
    () => ({ pricingTier: "Gold", onboardingStatus: "Onboarding" }),
    [],
  );
  const { values, setValue, ctx, sections, required, requiredFilled } =
    usePanelDraft(initialValues, addCorrespondentSections);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const nameOk = !!values.companyName?.trim();

  const save = async () => {
    if (!nameOk) return;
    setBusy(true);
    setError(null);
    try {
      const created = await correspondentsApi.create(
        prune({ ...values, companyName: values.companyName.trim() }),
      );
      await onCreated?.(created);
    } catch (e) {
      setError(errorText(e, "Create failed."));
      setBusy(false);
    }
  };

  return (
    <CorrespondentPanel
      title="Add Correspondent"
      icon={<BusinessOutlined />}
      onClose={onClose}
      error={error}
      footer={
        <FormPanelFooter
          required={required.length}
          filled={nameOk ? requiredFilled : 0}
          readyText="Ready to add"
        >
          <PanelActions
            onClose={onClose}
            onSave={save}
            disabled={!nameOk}
            busy={busy}
            label="Add correspondent"
          />
        </FormPanelFooter>
      }
    >
      <PanelSections sections={sections} ctx={ctx} setValue={setValue} />
    </CorrespondentPanel>
  );
}

AddCorrespondentPanel.propTypes = {
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
};
